import BaseCarouselWithMultipleSlides from "@PegasusCore/pegasus-one-carousel-with-multiple-slides/src/script";
import wheel from "wheel";
import normalizeWheel from "normalize-wheel";

class CarouselWithMultipleSlides extends BaseCarouselWithMultipleSlides {
    selectedSlideId = null;
    autoAdvance = false;
    tickerSpeed = null;

    tabContainer = null;
    tabOverflow = false;
    tabOverflowStart = true;
    tabOverflowEnd = false;

    sliderStart = true;
    sliderEnd = false;

    isAnimating = false;

    constructor(preview, autoplay, infinite, align, scroll, direction) {
        super(preview, autoplay, infinite, align);

        this.autoAdvance = scroll;
        this.tickerSpeed = direction;
    }

    init() {
        super.init();

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    window.dispatchEvent(new Event('resize'));
                    observer.disconnect(); // Only trigger once
                }
            });
        }, { threshold: 0.1 });

        //for flickity
        observer.observe(this.sliderContainer);

        this.tabContainer = this.$el.querySelector(
            ".Slide__Navigation__Container > div"
        );

        if (this.tabContainer != null) {
            this.tabContainer.addEventListener("scroll", (event) => {
                setTimeout(() => {
                    this.checkTabPosition();
                }, 500);
            });

            pegasus.onResizeFinished(() => {
                this.checkTabPosition();
            }, true);
        }

        if (this.autoAdvance) {
            let update = null;

            update = () => {
                this.slider.x =
                    (this.slider.x - this.tickerSpeed) %
                    this.slider.slideableWidth;
                this.slider.selectedIndex = this.slider.dragEndRestingSelect();
                this.slider.updateSelectedSlide();
                this.slider.settle(this.slider.x);
                window.requestAnimationFrame(update);
            };

            if (!this.sliderStarted) {
                setTimeout(() => {
                    update();
                }, 700);
            }
            this.sliderStarted = true;

            this.sliderContainer.addEventListener("mouseenter", () => {
                this.tickerSpeed = 0;
            });

            this.sliderContainer.addEventListener("mouseleave", () => {
                this.tickerSpeed = 0.6;
            });
        }

        if (this.slides && this.slides.length) {
            this.selectedSlideId = this.slides[0].id;
        }

        const introSection = this.$el.querySelector(".Introduction__Section");

        if (introSection) {
            pegasus.onResizeFinished(() => {
                this.realignArrows(introSection);
            }, true);
        }

        this.slider.on("change", () => {
            this.selectedSlideId = this.slider.selectedElement.id;
        });

        this.slider.on("settle", () => {
            //this.selectedSlideId = this.slider.selectedElement.id;

            setTimeout(() => {
                this.isAnimating = false;
            }, 500);
        });

        if (!this.autoAdvance) {
            wheel.addWheelListener(
                this.sliderContainer,
                (event) => {
                    const wheelNormalized = normalizeWheel(event);

                    // Define thresholds
                    const horizontalScrollThreshold = 0.8;

                    // Calculate horizontal ratio
                    const horizontalRatio =
                        Math.abs(wheelNormalized.pixelX) /
                        (Math.abs(wheelNormalized.pixelX) +
                            Math.abs(wheelNormalized.pixelY));

                    // Explicitly handle vertical scrolling
                    if (horizontalRatio < horizontalScrollThreshold) {
                        event.stopPropagation();
                        if (this.getSafariVersion() !== null) {
                            window.scroll(
                                window.scrollX,
                                window.scrollY + wheelNormalized.pixelY
                            );
                        }
                        return;
                    }

                    // Prevent default browser behaviour for horizontal scrolling
                    event.preventDefault();

                    this.slider.on("select", () => {
                        this.isAnimating = true;
                    });

                    if (this.isAnimating) {
                        return;
                    }

                    // Handle horizontal scrolling logic
                    if (
                        Math.abs(this.slider.x) + wheelNormalized.pixelX + 20 >
                            this.slider.slideableWidth ||
                        Math.abs(this.slider.x) + wheelNormalized.pixelX < 0
                    ) {
                        return;
                    }

                    if (wheelNormalized.pixelX > 0) {
                        this.slider.next();
                    } else {
                        this.slider.previous();
                    }
                },
                { passive: false }
            );
        }

        this.whichSlide();

        this.$nextTick(() => {
            //resize to trigger resize/reinit of flickity
            window.dispatchEvent(new Event("resize"));
        });
    }

    getSafariVersion() {
        const userAgent = navigator.userAgent;

        // Check if the user agent is Safari (but not Chrome, which also contains "Safari" in its user agent)
        const safariMatch = userAgent.match(/Version\/(\d+\.\d+).*Safari/);

        if (safariMatch) {
            return safariMatch[1]; // The first capture group is the version number
        } else {
            return null; // Not Safari
        }
    }

    whichSlide() {
        this.slider.on("change", (slide) => {
            this.currentSlide = slide;
            if (this.currentSlide === 0) {
                this.sliderStart = true;
            } else {
                this.sliderStart = false;
            }
        });
        this.slider.on("scroll", (e) => {
            this.sliderEnd = e.toFixed(2) >= 1;
        });
    }

    checkTabPosition() {
        this.tabOverflow =
            this.tabContainer.scrollWidth > this.tabContainer.clientWidth;

        this.tabOverflowStart = this.tabContainer.scrollLeft <= 5;
        this.tabOverflowEnd =
            this.tabContainer.scrollLeft >=
            this.tabContainer.scrollWidth - this.tabContainer.clientWidth - 5;
    }

    scrollTabsLeft() {
        if (!this.tabOverflowEnd) {
            this.tabContainer.scrollLeft += 150;
        }

        setTimeout(() => {
            this.checkTabPosition();
        }, 500);
    }

    scrollTabsRight() {
        if (!this.tabOverflowStart) {
            this.tabContainer.scrollLeft -= 150;
        }
        setTimeout(() => {
            this.checkTabPosition();
        }, 500);
    }

    realignArrows(introSection) {
        const arrows = this.$el.querySelector(".Navigation__Arrows");

        if (!arrows || !arrows.classList.contains("vertical-top")) {
            return;
        }

        const introHeight = introSection.offsetHeight;

        arrows.style.top = `${introHeight}px`;

        if (window.innerWidth < 600) {
            arrows.classList.remove("!-translate-y-full");
            arrows.classList.remove("!-translate-x-1/2");
            return;
        }
        arrows.classList.add("!-translate-y-full");
        arrows.classList.add("!-translate-x-1/2");
    }

    goToSlide(index, e) {
        // Get the index of the slide with the id that is the same value as index
        const slideIndex = this.slides.findIndex((slide) => slide.id === index);
        // If the slide is not found, return
        if (slideIndex === -1) return;

        this.slider.select(slideIndex);

        this.selectedSlideId = index;

        e.target.scrollIntoView({ block: "nearest", inline: "nearest" });
    }

    /**
     * containerResize
     *
     * Description: In order to get the slider to break out of its parent container
     * we need to set the width of the slider to be larger than the container.
     *
     * @return void
     */
    containerResize() {
        const container = this.$root.closest(".container");

        if (!container || window.outerWidth < 768) return;

        const slider = this.$root.querySelector(".slider");

        if (!slider) return;

        if (!this.align || this.align === "" || this.align === "center") {
            slider.style.width = "";
            return;
        }

        // We need to get the width of the container excluding padding
        let widthWithPaddings = container.clientWidth;
        const elementComputedStyle = window.getComputedStyle(container, null);

        // If the align is full, center or wide we need to set the slider to be the full width of the container
        let divider =
            this.align === "full" ||
            this.align === "center" ||
            this.align === "wide"
                ? 1
                : 2;

        const containerWidth =
            widthWithPaddings -
            parseFloat(elementComputedStyle.paddingLeft) -
            parseFloat(elementComputedStyle.paddingRight);

        const containerGutter = (window.innerWidth - containerWidth) / divider;

        const containerVw = (containerWidth / window.innerWidth) * 100;

        slider.style.width = `${window.innerWidth - containerGutter}px`;

        // In the page editor we use a simple scroll to display the slider instead of flickity so we need to make sure the slider is aligned properly.
        if (this.preview) {
            if (this.align === "right") {
                slider.style.marginLeft = "";
                slider.style.marginRight = `-${
                    (100 - containerVw) / divider
                }vw`;
            }

            if (this.align === "left") {
                slider.style.marginRight = "";
                slider.style.marginLeft = `-${(100 - containerVw) / divider}vw`;
            }
        }
    }
}

document.addEventListener("alpine:init", () => {
    Alpine.data(
        "carouselWithMultipleSlides",
        (preview, autoplay, infinite, align, scroll, direction) =>
            new CarouselWithMultipleSlides(
                preview,
                autoplay,
                infinite,
                align,
                scroll,
                direction
            )
    );
});
